/* eslint-disable no-shadow */
import { PueblaPreviousYearsResponse } from 'Services/sat/types';
import { PreviousYearStatus } from 'Containers/PreviousYears/types';
import { TaxReturnTypes } from 'Containers/Result/utils';
import { ResultType } from 'Types/common';
import { isMaiorAAResultAndIsToPay } from '../ResultInfoNotToReturn/utils';

export enum questionKeys {
  TO_RETURN_NOT_MANDATORY_WITH_PY = 'toReturnNotMandatoryWithPy',
  TO_RETURN_NOT_MANDATORY_WITHOUT_PY = 'toReturnNotMandatoryWithoutPy',
  TO_RETURN_MANDATORY_WITH_PY = 'toReturnMandatoryWithPy',
  TO_RETURN_MANDATORY_WITHOUT_PY = 'toReturnMandatoryWithoutPy',
  EQUAL_NOT_MANDATORY_WITH_PY = 'equalNotMandatoryWithPy',
  EQUAL_NOT_MANDATORY_WITHOUT_PY = 'equalNotMandatoryWithoutPy',
  EQUAL_MANDATORY_WITH_PY = 'equalMandatoryWithPy',
  EQUAL_MANDATORY_WITHOUT_PY = 'equalMandatoryWithoutPy',
  TO_PAY_NOT_MANDATORY_WITH_PY = 'toPayNotMandatoryWithPy',
  TO_PAY_NOT_MANDATORY_WITHOUT_PY = 'toPayNotMandatoryWithoutPy',
  TO_PAY_MANDATORY_WITH_PY = 'toPayMandatoryWithPy',
  TO_PAY_MANDATORY_WITHOUT_PY = 'toPayMandatoryWithoutPy',
  USER_WITH_MAIOR_RESULT_AND_IS_TO_PAY = 'userWithMaiorResultAndIsToPay',
}

export const getQuestionType = (data: ResultType, pyInfo?: PueblaPreviousYearsResponse): string => {
  const hasPy =
    pyInfo?.refundStatusType === TaxReturnTypes.TO_RETURN &&
    pyInfo?.stage === PreviousYearStatus.READY;
  const isMandatory = data.mandatory;

  if (data.taxReturnType === TaxReturnTypes.TO_RETURN) {
    if (isMandatory) {
      return hasPy
        ? questionKeys.TO_RETURN_MANDATORY_WITH_PY
        : questionKeys.TO_RETURN_MANDATORY_WITHOUT_PY;
    }
    return hasPy
      ? questionKeys.TO_RETURN_NOT_MANDATORY_WITH_PY
      : questionKeys.TO_RETURN_NOT_MANDATORY_WITHOUT_PY;
  }

  if (data.taxReturnType === TaxReturnTypes.EQUAL) {
    if (isMandatory) {
      return hasPy ? questionKeys.EQUAL_MANDATORY_WITH_PY : questionKeys.EQUAL_MANDATORY_WITHOUT_PY;
    }
    return hasPy
      ? questionKeys.EQUAL_NOT_MANDATORY_WITH_PY
      : questionKeys.EQUAL_NOT_MANDATORY_WITHOUT_PY;
  }

  if (
    data.taxReturnType === TaxReturnTypes.TO_PAY &&
    hasPy &&
    isMaiorAAResultAndIsToPay(data, pyInfo)
  ) {
    return questionKeys.USER_WITH_MAIOR_RESULT_AND_IS_TO_PAY;
  }

  if (isMandatory) {
    return hasPy ? questionKeys.TO_PAY_MANDATORY_WITH_PY : questionKeys.TO_PAY_MANDATORY_WITHOUT_PY;
  }
  return hasPy
    ? questionKeys.TO_PAY_NOT_MANDATORY_WITH_PY
    : questionKeys.TO_PAY_NOT_MANDATORY_WITHOUT_PY;
};

const commonToReturnQuestions = [
  {
    question: 'Result.toReturn.first.question',
    answer: 'Result.toReturn.first.answer',
  },
  { question: 'Result.toReturn.second.question', answer: 'Result.toReturn.second.answer' },
  { question: 'Result.toReturn.third.question', answer: 'Result.toReturn.third.answer' },
];

const commonEqualQuestions = [
  {
    question: 'Result.equal.first.question',
    answer: 'Result.equal.first.answer',
  },
  { question: 'Result.equal.second.question', answer: 'Result.equal.second.answer' },
];

const commonToPayQuestions = [
  {
    question: 'Result.toPay.first.question',
    answer: 'Result.toPay.first.answer',
  },
  { question: 'Result.toPay.second.question', answer: 'Result.toPay.second.answer' },
  { question: 'Result.toPay.third.question', answer: 'Result.toPay.third.answer' },
];

export const frecuentQuestionsByType = {
  [questionKeys.TO_RETURN_NOT_MANDATORY_WITH_PY]: {
    questionsAndAnswers: [
      ...commonToReturnQuestions,
      {
        question: 'Result.toReturnNotMandatoryWithPy.question',
        answer: 'Result.toReturnNotMandatoryWithPy.answer',
      },
    ],
  },
  [questionKeys.TO_RETURN_NOT_MANDATORY_WITHOUT_PY]: {
    questionsAndAnswers: commonToReturnQuestions,
  },
  [questionKeys.TO_RETURN_MANDATORY_WITH_PY]: {
    questionsAndAnswers: [
      ...commonToReturnQuestions,
      {
        question: 'Result.toReturnMandatoryWithPy.question',
        answer: 'Result.toReturnMandatoryWithPy.answer',
      },
    ],
  },
  [questionKeys.TO_RETURN_MANDATORY_WITHOUT_PY]: {
    questionsAndAnswers: commonToReturnQuestions,
  },
  [questionKeys.EQUAL_NOT_MANDATORY_WITH_PY]: {
    questionsAndAnswers: [
      ...commonEqualQuestions,
      {
        question: 'Result.equalNotMandatoryWithPy.question',
        answer: 'Result.equalNotMandatoryWithPy.answer',
      },
    ],
  },
  [questionKeys.EQUAL_NOT_MANDATORY_WITHOUT_PY]: {
    questionsAndAnswers: commonEqualQuestions,
  },
  [questionKeys.EQUAL_MANDATORY_WITH_PY]: {
    questionsAndAnswers: [
      ...commonEqualQuestions,
      {
        question: 'Result.equalMandatoryWithPy.question',
        answer: 'Result.equalMandatoryWithPy.answer',
      },
    ],
  },
  [questionKeys.EQUAL_MANDATORY_WITHOUT_PY]: {
    questionsAndAnswers: commonEqualQuestions,
  },
  [questionKeys.TO_PAY_NOT_MANDATORY_WITH_PY]: {
    questionsAndAnswers: [
      ...commonToPayQuestions,
      {
        question: 'Result.toPayNotMandatoryWithPy.question',
        answer: 'Result.toPayNotMandatoryWithPy.answer',
      },
    ],
  },
  [questionKeys.TO_PAY_NOT_MANDATORY_WITHOUT_PY]: {
    questionsAndAnswers: commonToPayQuestions,
  },
  [questionKeys.TO_PAY_MANDATORY_WITH_PY]: {
    questionsAndAnswers: [
      {
        question: 'Result.toPay.first.question',
        answer: 'Result.toPay.first.answer',
      },
      { question: 'Result.toPay.third.question', answer: 'Result.toPay.third.answer' },
      {
        question: 'Result.toPayMandatoryWithPy.question',
        answer: 'Result.toPayMandatoryWithPy.answer',
      },
    ],
  },
  [questionKeys.TO_PAY_MANDATORY_WITHOUT_PY]: {
    questionsAndAnswers: [
      {
        question: 'Result.toPay.first.question',
        answer: 'Result.toPay.first.answer',
      },
      { question: 'Result.toPay.third.question', answer: 'Result.toPay.third.answer' },
      {
        question: 'Result.toPayMandatoryWithoutPy.second.question',
        answer: 'Result.toPayMandatoryWithoutPy.second.answer',
      },
    ],
  },
  [questionKeys.USER_WITH_MAIOR_RESULT_AND_IS_TO_PAY]: {
    questionsAndAnswers: [
      {
        question: 'Result.toPay.first.question',
        answer: 'Result.toPay.first.answer',
      },
      { question: 'Result.toPay.third.question', answer: 'Result.toPay.third.answer' },
      {
        question: 'Result.toPayMandatoryWithoutPy.second.question',
        answer: 'Result.toPayMandatoryWithoutPy.second.answer',
      },
    ],
  },
};
